import { CustomElement, OverrideCustomElementConfig } from "@cervval.js/client-html5-core/src/components/customElement.decorator";
import { TypedHtmlElements } from "@cervval.js/client-html5-core/src/components/typedHtmlElements.decorator";
import { TypedWebComponentOptions, WebComponentOptions } from "@cervval.js/client-html5-core/src/components/typedWebComponentOptions.decorator";
import { FLEX_CONTAINER_COLUMN } from "@cervval.js/client-html5-core/src/tools/cssClasses.tools";
import { AcceptInputValueType, CjsInput } from "../../components/inputs/input";
import { CjsInputSearch, InputSearchCommonOptions } from "../../components/inputs/inputSearch/inputSearch";
import { CjsEditor, EditorOptions, defaultEditorOptions } from "../editor";

export type InputSearchEditorOptions = EditorOptions;

export const defaultInputSearchEditorOptions: InputSearchEditorOptions = {
    ...defaultEditorOptions
};

@CustomElement({
    name: "cjs-input-search-editor",
    template: require("./inputSearchEditor.html"),
    elementCssClasses: ["cjs-editor", FLEX_CONTAINER_COLUMN]
})
export class CjsInputSearchEditor<ItemType, ItemValueType, ValueType extends AcceptInputValueType = AcceptInputValueType> extends CjsEditor<ValueType> {

    /** Attributes options of InputSearchEditor */
    @TypedWebComponentOptions(defaultInputSearchEditorOptions)
    public readonly options!: WebComponentOptions<InputSearchEditorOptions>;

    /** Components */
    @TypedHtmlElements({ ensureLoadedClasses: [CjsInputSearch] })
    public readonly components!: {
        inputSearch: CjsInputSearch<ItemType, ItemValueType, ValueType>
    };

    /** Direct access to input search component */
    public get inputSearch(): CjsInputSearch<ItemType, ItemValueType, ValueType> {
        return this.components.inputSearch;
    }

    constructor(options?: Partial<InputSearchEditorOptions>, inputSearchCommonOptions?: Partial<InputSearchCommonOptions<ItemType, ItemValueType, ValueType>>, customElementOptions?: OverrideCustomElementConfig) {
        super(customElementOptions);

        // Update options
        this.options.setWithoutRefresh(options);
        this.inputSearch.inputOptions.setWithoutRefresh(inputSearchCommonOptions?.input);
        this.inputSearch.options.setWithoutRefresh(inputSearchCommonOptions?.inputSearch);
        this.inputSearch.smartSelectOptions.setWithoutRefresh(inputSearchCommonOptions?.smartSelect);
    }

    /** Get input of Editor, where user set data */
    public getInput(): CjsInput<ValueType> {
        return this.inputSearch.input;
    }

    /**
     * Initialize components, input components and callbacks after the element is added to the dom and _components is ready
     */
    public override init(): void {
        super.init();

        // Callback for input search change event
        this.inputSearch.changeEvent.on(_ => this._onInputValueChange());
    }

    /** Refresh component display */
    public override refresh(): void {

        // Refresh
        super.refresh();

        // Set options
        this.inputSearch.refresh();
    }

    /** Set formCheckInput as validation form element */
    public override getValidationFormElement(): HTMLElement {
        return this.getInput().input;
    }

    /**
     * Update the input value from stored value
     */
    protected _refreshInputValue(): void {
        this.inputSearch.setSearchValue(this._value);
    }

    /**
     * Gets the value corresponding to the current input value
     */
    protected _getValueFromInputComponent(): ValueType | undefined {
        return this.inputSearch.getSearchValue();
    }

    /** Update disabled attribute on input component */
    protected _refreshDisabled(): void {
        this.inputSearch.inputOptions.setAndRefreshIfChanged({ disabled: this.disabled });
    }

    /** Open the select if needed than trigger onDone when value has changed */
    public override tryEdit(onDone: () => void): void {
        // Register callback when value has changed
        this.changeEvent.on(onDone);// Change event callback triggered when user click on value

        // Open the select if needed for direct access to the selection
        if (!this.inputSearch.smartSelect.isOpen()) {
            this.inputSearch.smartSelect.open();
        }
    }
}